import React, { useState } from 'react';
import { STYLE_WORDS } from '../../pages/EmployeePortal/Warmups/warmupShape';
import {
	cellExists,
	DURATIONS,
	LEVELS,
	AGE_BANDS,
	STYLES,
	VOICES,
	DURATION_WORDS,
	VOICE_WORDS,
} from '../../services/warmupRoutineModel';
import { LEVEL_LABEL, cellSummary, mmss } from './warmupFormat';

/**
 * The routine's header chrome — unsaved-changes draft header, the last-lesson
 * line, the settings (behind a disclosure when compact), and the two alert
 * rows.
 *
 * Renders a FRAGMENT of siblings, because that is exactly what these were
 * inside WarmupPlayer's <section> before the 2026-09-04 split (Lesson Dashboard
 * I1 phase 0) — keeping them a fragment is what makes the DOM identical rather
 * than merely equivalent. I1 moves this block into the right column above the
 * routine list; nothing about it changes there.
 */
const WarmupSetup = ({ wp, draft = null, headerLine = '', compact = false, extraControls = null }) => {
	const { routine, cell, items, loadError, changeCell, showRebuild, rebuild } = wp;
	const [showSetup, setShowSetup] = useState(!compact);

	return (
		<>
			{draft && draft.dirty && (
				<div className="wp__draft" role="status">
					<div className="wp__draft__head">
						<i className="fa-sharp fa-solid fa-pen-to-square" aria-hidden="true" />
						<b>Unsaved changes</b>
						<span className="wp__draft__actions">
							<button type="button" className="wp__mini wp__mini--go" onClick={draft.onSave} disabled={draft.saving}>
								{draft.saving ? 'Saving…' : 'Save routine'}
							</button>
							<button type="button" className="wp__mini" onClick={draft.onDiscard} disabled={draft.saving}>
								Discard
							</button>
						</span>
					</div>
					{draft.changes && draft.changes.length > 0 && (
						<ul className="wp__draft__list">
							{draft.changes.map((line) => (
								<li key={line}>{line}</li>
							))}
						</ul>
					)}
					{draft.error && <p className="ps-alert ps-alert--danger wp__alert">{draft.error}</p>}
				</div>
			)}

			{headerLine && <p className="wp__lastlesson">{headerLine}</p>}

			{compact && (
				<button type="button" className="wp__disclose" aria-expanded={showSetup} onClick={() => setShowSetup((v) => !v)}>
					<i className={`fa-sharp fa-solid fa-chevron-${showSetup ? 'up' : 'down'}`} aria-hidden="true" />
					Routine settings · {cellSummary(cell, DURATION_WORDS)}
				</button>
			)}

			{showSetup && (
				<div className="wp__setup">
					<label className="wp__field">
						<span>Length</span>
						<select className="ps-select" value={cell.duration} onChange={(e) => changeCell('duration', e.target.value)}>
							{DURATIONS.map((d) => (
								<option key={d} value={d}>
									{DURATION_WORDS[d]}
								</option>
							))}
						</select>
					</label>
					<label className="wp__field">
						<span>Level</span>
						<select className="ps-select" value={cell.level} onChange={(e) => changeCell('level', e.target.value)}>
							{LEVELS.map((l) => (
								<option key={l} value={l} disabled={!cellExists({ ...cell, level: l })}>
									{LEVEL_LABEL[l]}
								</option>
							))}
						</select>
					</label>
					<label className="wp__field">
						<span>Ages</span>
						<select className="ps-select" value={cell.ageBand} onChange={(e) => changeCell('ageBand', e.target.value)}>
							{AGE_BANDS.map((a) => (
								<option key={a} value={a}>
									{a}
								</option>
							))}
						</select>
					</label>
					<label className="wp__field">
						<span>Style</span>
						<select className="ps-select" value={cell.style} onChange={(e) => changeCell('style', e.target.value)}>
							{STYLES.map((s) => (
								<option key={s} value={s} disabled={!cellExists({ ...cell, style: s })}>
									{STYLE_WORDS[s] || s}
								</option>
							))}
						</select>
					</label>
					<label className="wp__field">
						<span>Voice</span>
						<select className="ps-select" value={cell.voice} onChange={(e) => changeCell('voice', e.target.value)}>
							{VOICES.map((v) => (
								<option key={v} value={v}>
									{VOICE_WORDS[v]}
								</option>
							))}
						</select>
					</label>
					{/* The transport controls the strip drops as it narrows (I1 phase 3).
					    They live behind THIS disclosure, which is the design's placement
					    and is not a hidden control: the row is always rendered, always
					    labelled, and one tap opens it — unlike a hover affordance, which
					    has no equivalent on the installed PWA and is the hardest thing to
					    find for the primary user. Rendering them loose above the routine
					    instead was tried and looked like a wall of buttons between the
					    teacher and the exercises. */}
					{extraControls}

					<div className="wp__summary">
						<b>{items.length} exercises</b> · about {mmss(routine?.seconds || 0)}
						{showRebuild && (
							<button type="button" className="wp__rebuild" onClick={() => rebuild(cell)}>
								Rebuild
							</button>
						)}
					</div>
				</div>
			)}

			{routine?.error && <p className="ps-alert ps-alert--info wp__alert">{routine.error}</p>}
			{loadError && <p className="ps-alert ps-alert--danger wp__alert">{loadError}</p>}
		</>
	);
};

export default WarmupSetup;
